import { addDays, addMonths, differenceInCalendarDays, endOfMonth, format, startOfMonth, startOfQuarter, subMonths } from "date-fns";
import type { Schedule } from "@/store/appStore";
import type { CustomerDocument } from "@/types/documentTypes";
import type { PostDisbursementCase } from "@/types/postDisbursement";

export type DashboardRange = "6m" | "this_month" | "this_quarter" | "current_fy" | "previous_fy" | "next_fy";

export const historicDashboardRanges: Array<{ value: DashboardRange; label: string }> = [
  { value: "this_month", label: "This Month" }, { value: "this_quarter", label: "This Quarter" },
  { value: "current_fy", label: "Current FY" }, { value: "previous_fy", label: "Previous FY" },
];
export const forecastDashboardRanges = historicDashboardRanges.map(item => item.value === "previous_fy" ? { value: "next_fy" as const, label: "Next FY" } : item);

export interface MonthBucket {
  key: string;
  label: string;
  start: Date;
  end: Date;
}

export const scheduleAssetValue = (schedule: Schedule) =>
  schedule.assets.reduce((sum, asset) => sum + (asset.total ?? asset.basePrice * (asset.quantity ?? 1)), 0);

export function dashboardMonths(range: DashboardRange, now = new Date()): MonthBucket[] {
  let first: Date;
  let count: number;
  if (range === "this_month") {
    first = startOfMonth(now);
    return Array.from({ length: differenceInCalendarDays(endOfMonth(now), first) + 1 }, (_, index) => {
      const start = addDays(first, index);
      return { key: format(start, "yyyy-MM-dd"), label: format(start, "dd MMM"), start, end: new Date(addDays(start, 1).getTime() - 1) };
    });
  } else if (range === "this_quarter") {
    first = startOfQuarter(now);
    count = 3;
  } else if (range === "6m") {
    first = startOfMonth(subMonths(now, 5));
    count = 6;
  } else {
    const currentFyYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const year = range === "previous_fy" ? currentFyYear - 1 : range === "next_fy" ? currentFyYear + 1 : currentFyYear;
    first = new Date(year, 3, 1);
    count = 12;
  }
  return Array.from({ length: count }, (_, index) => {
    const start = startOfMonth(addMonths(first, index));
    const next = startOfMonth(addMonths(first, index + 1));
    return { key: format(start, "yyyy-MM"), label: format(start, "MMM yy"), start, end: new Date(next.getTime() - 1) };
  });
}

const inMonth = (date: Date, month: MonthBucket) => date >= month.start && date <= month.end;

export function customerDisbursementRecords(
  schedules: Schedule[] | undefined | null,
  cases: PostDisbursementCase[] | undefined | null,
) {
  const scheduleList = schedules ?? [];
  const caseList = cases ?? [];
  const eligible = scheduleList.filter(schedule => schedule.status === "Approved" || schedule.status === "Active")
    .map(schedule => ({ schedule, date: new Date(caseList.find(item => item.rsId === schedule.id)?.disbursementDate || schedule.createdAt || schedule.config.commencementDate) }))
    .filter(item => Number.isFinite(item.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime() || a.schedule.id.localeCompare(b.schedule.id));
  const seen = new Set<string>();
  return eligible.map(({ schedule, date }) => {
    const customerId = schedule.config.customer.customerId;
    const status = seen.has(customerId) ? "Repeat" : "New";
    seen.add(customerId);
    return { id: schedule.id, date, customerName: schedule.config.customer.name, amount: scheduleAssetValue(schedule), status };
  });
}

export function customerDisbursementDetails(
  schedules: Schedule[] | undefined | null,
  cases: PostDisbursementCase[] | undefined | null,
  range: DashboardRange,
) {
  const months = dashboardMonths(range);
  return customerDisbursementRecords(schedules, cases).filter(record => months.some(month => inMonth(record.date, month)));
}

export function buildCustomerDisbursementTrend(
  schedules: Schedule[] | undefined | null,
  cases: PostDisbursementCase[] | undefined | null,
  range: DashboardRange,
  mode: "amount" | "count" = "amount",
) {
  const eligible = customerDisbursementRecords(schedules, cases);

  return dashboardMonths(range).map((month) => {
    let newCustomer = 0;
    let repeatCustomer = 0;
    eligible.filter(({ date }) => inMonth(date, month)).forEach(record => {
      const value = mode === "count" ? 1 : record.amount;
      if (record.status === "New") newCustomer += value;
      else repeatCustomer += value;
    });
    return { month: month.label, newCustomer: Math.round(newCustomer), repeatCustomer: Math.round(repeatCustomer) };
  });
}

export function buildSigningTrend(
  schedules: Schedule[] | undefined | null,
  documents: CustomerDocument[] | undefined | null,
  range: DashboardRange,
) {
  const uniqueMra = new Map<string, Date>();
  (schedules ?? []).forEach((schedule) => {
    if (!schedule.config.mraSignedDate) return;
    const key = `${schedule.config.customer.customerId}:${schedule.config.mraSignedDate}`;
    uniqueMra.set(key, new Date(schedule.config.mraSignedDate));
  });
  const termsheets = (documents ?? [])
    .filter((document) => document.documentType === "signed_termsheet" && document.signedDate)
    .map((document) => new Date(document.signedDate ?? document.uploadedDate));

  return dashboardMonths(range).map((month) => ({
    month: month.label,
    mra: [...uniqueMra.values()].filter((date) => inMonth(date, month)).length,
    termsheet: termsheets.filter((date) => inMonth(date, month)).length,
  }));
}

export function buildBuybackTrend(
  schedules: Schedule[] | undefined | null,
  range: DashboardRange,
) {
  return dashboardMonths(range).map((month) => {
    let amount = 0;
    let schedulesCount = 0;
    (schedules ?? []).forEach((schedule) => {
      if (schedule.status !== "Approved" && schedule.status !== "Active") return;
      const maturity = schedule.lines.at(-1)?.toDate;
      if (!maturity || !inMonth(new Date(maturity), month)) return;
      amount += scheduleAssetValue(schedule) * ((schedule.config.residualPercent || 1) / 100);
      schedulesCount += 1;
    });
    return { month: month.label, amount: Math.round(amount), schedules: schedulesCount };
  });
}